import * as React from 'react';
import { ActionButton, Icon, Label, PrimaryButton, Spinner, SpinnerSize } from '@fluentui/react';
import ReactTooltip from 'react-tooltip';
import FileIcon from '../FileIcon/FileIcon';
import OneDrivePicker, { ISelectedItem } from '../shared/OneDrivePicker';
import { IDeliveryFile } from '../../models/IDeliveryFile';

export interface IUploadedFile {
    UniqueId: string;
    FileName: string;
    FileUrl?: string;
}

export interface IUploadFileFormProps {
    uploadedFiles: IDeliveryFile[];

    // DeviceFiles 
    isLoadingFiles: boolean;

    // uploadedFiles: IUploadedFile[];
    filesLoading: string[];
    errorFetchingFiles: boolean;


    SemiBoldTitle?: boolean;
    CanBeEdited?: boolean;
    enableDeviceUpload?: boolean;
    enableOneDrivePicker?: boolean;
    enableFilePreview?: boolean;
    // fetchFiles(): void | Promise<void>;
    deleteUploadedFile(file: IDeliveryFile): void | Promise<void>;
    submitFiles(files: File[]): void | Promise<void>;
    onUploadedFileSelect?(file: IDeliveryFile): void | Promise<void>;
    onOneDriveSelect?(selectedItem: ISelectedItem): void | Promise<void>;
}

export interface IUploadFileFormState {
    filesToUpload: File[];
    selectedUploadedFileUniqueId?: string;
}

export default class UploadFileForm extends React.Component<IUploadFileFormProps, IUploadFileFormState> {

    private fileInputRef = React.createRef<HTMLInputElement>();

    state: IUploadFileFormState = {
        filesToUpload: [],
        selectedUploadedFileUniqueId: undefined
    };

    handleUploadedFileSelected = async (file: IDeliveryFile) => {
        const isAlreadySelected = this.state.selectedUploadedFileUniqueId === file.FileUniqueId;

        if (isAlreadySelected) {
            this.setState({ selectedUploadedFileUniqueId: undefined });
            return;
        }

        this.setState({ selectedUploadedFileUniqueId: file.FileUniqueId });

        if (this.props.onUploadedFileSelect) {
            await this.props.onUploadedFileSelect(file);
        }
    }

    removePendingFile = (fileToRemove: File) => {
        this.setState((prevState) => ({
            filesToUpload: prevState.filesToUpload.filter((file) => {
                return !(
                    file === fileToRemove ||
                    (file.name === fileToRemove.name &&
                        file.size === fileToRemove.size &&
                        file.lastModified === fileToRemove.lastModified)
                );
            })
        }));
    }

    handleFilesSelected = (files: File[]) => {
        this.setState((prevState) => ({
            filesToUpload: [...prevState.filesToUpload, ...files]
        }));
    }

    filterDuplicateFilesByName = (files: File[]) => {
        const existingNames = new Set([
            ...this.props.uploadedFiles.map((file) => file.FileName.toLowerCase()),
            ...this.state.filesToUpload.map((file) => file.name.toLowerCase())
        ]);

        return files.filter((file) => {
            const fileName = file.name.toLowerCase();
            if (existingNames.has(fileName)) {
                return false;
            }

            existingNames.add(fileName);
            return true;
        });
    }

    handleSubmit = async () => {
        if (this.state.filesToUpload.length === 0) {
            return;
        }
        await this.props.submitFiles(this.state.filesToUpload);
        this.setState({ filesToUpload: [] });
    }

    async componentDidMount(): Promise<void> {
        // this.props.fetchFiles();
    }

    renderFetchError = () => {
        return (
            <div style={{ padding: '10px 0 0 0', display: 'flex', alignItems: 'center' }}>
                <Icon style={{ fontSize: 18, color: 'red' }} className="standard-icon" iconName="AlertSolid" />
                Noe gikk galt ved innhenting av filer
            </div>
        );
    }

    renderDeleteButton = (file: IDeliveryFile | File, isUploaded: boolean) => {
        if (isUploaded) {
            return (
                <ActionButton
                    className="deleteUploadFileButton"
                    iconProps={{ iconName: 'Delete' }}
                    onClick={async () => await this.props.deleteUploadedFile(file as IDeliveryFile)}
                >
                    Slett
                </ActionButton>
            );
        }
        return (
            <ActionButton
                iconProps={{ iconName: 'Delete' }}
                onClick={() => this.removePendingFile(file as File)}
            >
                Fjern
            </ActionButton>
        );
    }

    renderFileListBody() {
        const { uploadedFiles, filesLoading, CanBeEdited, onUploadedFileSelect, enableFilePreview = true } = this.props;
        const { filesToUpload, selectedUploadedFileUniqueId } = this.state;
        const unified: Array<IDeliveryFile | File> = [...uploadedFiles, ...filesToUpload];

        return (
            <div>
                {unified.map((file) => {
                    const isUploaded = !(file instanceof File);
                    const uniqueId = isUploaded ? (file as IDeliveryFile).FileUniqueId : (file as File).name;
                    const fileName = isUploaded ? (file as IDeliveryFile).FileName : (file as File).name;
                    const tooltipId = `${uniqueId}-${fileName}`;

                    return (
                        <div key={tooltipId} style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', minHeight: '40px', gap: '8px' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {isUploaded && onUploadedFileSelect && (
                                    <input
                                        type="radio"
                                        name="selectedUploadedFile"
                                        checked={selectedUploadedFileUniqueId === uniqueId}
                                        readOnly
                                        onClick={async () => await this.handleUploadedFileSelected(file as IDeliveryFile)}
                                    />
                                )}
                                <FileIcon fileName={fileName} size={16} />
                                {enableFilePreview && isUploaded && (file as IDeliveryFile).WopiViewUrl
                                    ? <a href={(file as IDeliveryFile).WopiViewUrl} target="_blank">{fileName}</a>
                                    : fileName
                                }
                                {CanBeEdited && (
                                    <span>
                                        {filesLoading.indexOf(uniqueId) < 0 && isUploaded
                                            ? this.renderDeleteButton(file, isUploaded)
                                            : <div style={{ minWidth: '50px' }}><Spinner size={SpinnerSize.medium} /></div>
                                        }
                                    </span>
                                )}
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderFileList() {
        const { isLoadingFiles, errorFetchingFiles, uploadedFiles } = this.props;
        // const { isLoadingFiles, errorFetchingFiles,  } = this.props;
        const { filesToUpload } = this.state;
        return (
            <div style={{ margin: '10px 0px 10px 0px', padding: '10px', backgroundColor: '#fff' }}>
                {isLoadingFiles
                    ? <Spinner size={SpinnerSize.large} />
                    : !errorFetchingFiles
                        ? <div>
                            {(uploadedFiles.length > 0 || filesToUpload.length > 0)
                                ? this.renderFileListBody()
                                : <div>Ingen filer er lagt til</div>
                            }
                        </div>
                        : this.renderFetchError()
                }
            </div>
        );
    }

    render() {
        const {
            SemiBoldTitle,
            CanBeEdited,
            filesLoading,
            enableDeviceUpload = true,
            enableOneDrivePicker = true,
            onOneDriveSelect
        } = this.props;
        const { filesToUpload } = this.state;
        return (
            <div style={{ margin: '20px 0px 10px 0px' }}>
                <Label>
                    {SemiBoldTitle
                        ? <div className="ms-font-m ms-fontWeight-semibold">Filer</div>
                        : <b>Filer</b>
                    }
                </Label>
                {this.renderFileList()}
                {CanBeEdited && (
                    <div style={{ margin: '0 0 20px 0', display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {enableOneDrivePicker && onOneDriveSelect && (
                            <OneDrivePicker
                                onClose={async (selected) => {
                                    await onOneDriveSelect(selected);
                                }}
                            />
                        )}

                        {enableDeviceUpload && (
                            <>
                                <input
                                    ref={this.fileInputRef}
                                    type="file"
                                    multiple
                                    style={{ display: 'none' }}
                                    onChange={async (e) => {
                                        console.log('ON CHANGE:', e);
                                        if (e.target.files && e.target.files.length > 0) {
                                            const selectedFiles = Array.from(e.target.files);
                                            const filesToAdd = this.filterDuplicateFilesByName(selectedFiles);

                                            if (filesToAdd.length === 0) {
                                                if (e.target && e.target.value) {
                                                    e.target.value = '';
                                                }
                                                return;
                                            }

                                            this.handleFilesSelected(filesToAdd);
                                            try {
                                                await this.props.submitFiles(filesToAdd);
                                                if (e.target && e.target.value) {
                                                    e.target.value = '';
                                                }
                                            }
                                            finally {
                                                this.setState({ filesToUpload: [] });
                                            }
                                        }
                                        else {
                                            console.log('No files selected:', e.target.files);
                                        }
                                    }}
                                />
                                <ActionButton
                                    iconProps={{ iconName: 'Attach' }}
                                    onClick={() => {
                                        if (this.fileInputRef.current) {
                                            this.fileInputRef.current.value = '';
                                            this.fileInputRef.current.click();
                                        }
                                    }}
                                >
                                    Last opp filer
                                </ActionButton>
                                {/* <PrimaryButton
                                    disabled={filesToUpload.length === 0 || filesLoading.length > 0}
                                    onClick={this.handleSubmit}
                                    text="Last opp"
                                    iconProps={{ iconName: 'Upload' }}
                                /> */}
                            </>
                        )}                        
                    </div>
                )}
            </div>
        );
    }
}
